import React, { useRef, useState } from 'react'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import AwsConfig from '../../Config/AwsConfig'
import MovieImagePresignedUrl from '../../Services/Queries/Movieimagepresignedurl'
import RenderGallery from './RenderGallery'
import GalleryInput from './GalleryInput'

const GalleryImages = props => {
  const {
    GalleryImages,
    inputData,
    sendinputdata,
    sendBooleanValueChangeDone,
    galleryType,
    ImageType,
    disableInput,
  } = props

  const [selectedImages, setSelectedImages] = useState([])
  const [imagePreviews, setImagePreviews] = useState([])
  const [selectedImageTitles, setSelectedImageTitles] = useState([])
  const [imageTags, setImageTags] = useState([]) // State to manage tags per image
  const currentDate = new Date()

  const dragItem = useRef()
  const dragOverItem = useRef()
  const removeImage = index => {
    const inputDataCopy = { ...inputData }
    const updatedGalleryImages = GalleryImages.filter((item, i) => i !== index)

    inputDataCopy.GalleryImages = updatedGalleryImages

    // Update imageUpdatedTime based on the number of remaining images
    if (updatedGalleryImages.length >= 1) {
      inputDataCopy.imageUpdatedTime = Date.parse(currentDate) / 1000
    } else {
      delete inputDataCopy.imageUpdatedTime
    }

    sendinputdata(inputDataCopy)

    sendBooleanValueChangeDone('GalleryImages', true)
  }

  const editImage = index => {
    const inputDataCopy = { ...inputData, imageUpdatedTime: Date.parse(currentDate) / 1000 }
    const galleryImagesCopy = [...GalleryImages]
    const editedValue = galleryImagesCopy.splice(index, 1)
    const imagePreviewsTemp = [...imagePreviews]
    imagePreviewsTemp.push(editedValue[0].path)
    const selectedImageTitlesTemp = [...selectedImageTitles]
    selectedImageTitlesTemp.push(editedValue[0].title)
    const imageTagsTemp = [...imageTags]
    imageTagsTemp.push(editedValue[0].galleryTags)

    setSelectedImageTitles(selectedImageTitlesTemp)
    setImagePreviews(imagePreviewsTemp)
    setImageTags(imageTagsTemp)

    inputDataCopy.GalleryImages = galleryImagesCopy

    sendinputdata(inputDataCopy)
    sendBooleanValueChangeDone('GalleryImages', true)
  }

  const dragStart = (e, position) => {
    dragItem.current = position
    inputData.imageUpdatedTime = Date.parse(currentDate) / 1000
  }

  const dragEnter = (e, position) => {
    inputData.imageUpdatedTime = Date.parse(currentDate) / 1000
    dragOverItem.current = position
  }

  const drop = () => {
    inputDataCopy.imageUpdatedTime = Date.parse(currentDate) / 1000
    const inputDataCopy = { ...inputData }
    const galleryImagesCopy = [...GalleryImages]
    const dragItemContent = galleryImagesCopy[dragItem.current]

    galleryImagesCopy.splice(dragItem.current, 1)
    galleryImagesCopy.splice(dragOverItem.current, 0, dragItemContent)

    inputDataCopy.GalleryImages = galleryImagesCopy
    sendBooleanValueChangeDone('GalleryImages', true)

    sendinputdata(inputDataCopy)
  }

  const selectImages = event => {
    const files = Array.from(event.target.files)
    const previews = []
    const titles = []
    const sizeErrors = []

    files.forEach((file, _index) => {
      if (file.size > 2 * 1024 * 1024) {
        // 2 MB in bytes
        sizeErrors.push(`Image ${file.name} exceeds the maximum size of 2 MB`)
      } else {
        const image = new Image()
        image.src = URL.createObjectURL(file)

        image.onload = () => {
          previews.push(image.src)
          titles.push('')
          setSelectedImages(prev => [...prev, file])
          setImagePreviews(prev => [...prev, image.src])
          setSelectedImageTitles(prev => [...prev, ''])
        }
      }
    })
  }

  const uploadSelectedImage = async image => {
    try {
      const randomId = await uuidv4()
      const moviePath = galleryType === 'person' ? AwsConfig.personPathUrl : AwsConfig.moviePathUrl
      const key = `${moviePath}${randomId}.jpeg`

      const params = { key, mimeType: 'image/jpeg' }
      const preSignedURLObject = await getPreSignedURL(params)

      const { fields, url: signedURL } = preSignedURLObject.data
      const formData = new FormData()

      Object.entries(fields).forEach(([key, value]) => formData.append(key, value))
      formData.append('file', image)

      await uploadToS3(signedURL, formData)

      const cdnurl = AwsConfig.movieCdnUrl
      const posterUrl = `${cdnurl}${key}`
      return posterUrl
    } catch (err) {
      console.warn(err, 'err')
    }
  }

  const getPreSignedURL = async params => {
    const preSignedURLObject = await MovieImagePresignedUrl(params)
    return JSON.parse(preSignedURLObject.signedObject)
  }

  const uploadToS3 = async (signedURL, formData) => {
    await axios({
      method: 'post',
      url: signedURL,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  const imageTitleChange = (event, index) => {
    const titles = [...selectedImageTitles]
    titles[index] = event.target.value
    setSelectedImageTitles(titles)
  }

  const updateTags = (index, tags) => {
    const imageTagsTemp = [...imageTags]
    imageTagsTemp[index] = tags
    setImageTags(imageTagsTemp)
  }

  const pushImagesToArray = async index => {
    if (!selectedImageTitles[index] || !imagePreviews[index]) {
      return
    }

    const inputDataCopy = { ...inputData }
    const galleryImagesCopy = [...GalleryImages]
    const imagesToUpload = [...selectedImages]

    try {
      let imagePath = ''
      if (imagesToUpload[index]) {
        imagePath = await uploadSelectedImage(imagesToUpload[index])
      } else {
        imagePath = imagePreviews[index]
      }

      galleryImagesCopy.unshift({
        title: selectedImageTitles[index],
        path: imagePath,
        galleryTags: imageTags[index] || [],
      })

      inputDataCopy.GalleryImages = galleryImagesCopy
      inputDataCopy.imageUpdatedTime = Date.parse(currentDate) / 1000
      sendinputdata(inputDataCopy)
      sendBooleanValueChangeDone('GalleryImages', true)
      setSelectedImages(prev => prev.filter((_, idx) => idx !== index))
      setImagePreviews(prev => prev.filter((_, idx) => idx !== index))
      setSelectedImageTitles(prev => prev.filter((_, idx) => idx !== index))
      setImageTags(prev => prev.filter((_, idx) => idx !== index))
    } catch (error) {
      console.error('Error uploading image:', error)
    }
  }

  return (
    <div>
      <label>Gallery Images:</label>
      <RenderGallery
        GalleryImages={GalleryImages}
        editImage={editImage}
        dragStart={dragStart}
        dragEnter={dragEnter}
        drop={drop}
        disableInput={disableInput}
        removeImage={removeImage}
      />
      <GalleryInput
        selectImages={selectImages}
        imagePreviews={imagePreviews}
        disableInput={disableInput}
        imageTitleChange={imageTitleChange}
        selectedImageTitles={selectedImageTitles}
        setImagePreviews={setImagePreviews}
        pushImagesToArray={pushImagesToArray}
        ImageType={ImageType}
        updateTags={updateTags}
        imageTags={imageTags}
      />
    </div>
  )
}

export default GalleryImages
